import {Link} from 'react-router-dom';
import {useDeletedPosts, usePostMutations} from '../api/posts';
import EmptyState from '../components/feedback/EmptyState';
import ErrorBox from '../components/feedback/ErrorBox';
import {SkeletonList} from '../components/feedback/Skeleton';
import Icon from '../components/Icon';
import TellRow from '../components/tell/TellRow';
import {confirm} from '../hooks/useConfirm';
import {toast} from '../hooks/useToast';
import Button from '../components/Button';

// Soft-deleted tells, recoverable for 30 days. Opening this list is also when the over-30-day
// ones are purged (a few per visit), in place of a Cron.
export default function RecentlyDeletedPage() {
    const deleted = useDeletedPosts();
    const {restore, purge} = usePostMutations();

    const daysLeft = (deletedAt: string, keepDays: number) => Math.max(0, keepDays - Math.floor((Date.now() - Date.parse(deletedAt)) / 86_400_000));

    const deleteForever = async (id: string, title: string) => {
        const done = await confirm({
            title: `Delete “${title}” for good?`,
            body: 'Its photos go too.',
            help: 'There is no way back after this: not from Recently deleted, not from an export made later.',
            confirmLabel: 'Delete forever',
            busyLabel: 'Deleting…',
            destructive: true,
            action: () => purge.mutateAsync(id)
        });
        if (done) toast.success('Gone.');
    };

    return (
        <div className="settle-in flex flex-col gap-5">
            <header className="pt-2">
                <Link to="/more" className="meta mb-1 inline-flex items-center gap-1 no-underline">
                    <Icon name="back" size={16} /> More
                </Link>
                <h1 className="display m-0">Recently deleted</h1>
                <p className="meta mt-1">Kept for {deleted.data?.keepDays ?? 30} days, then gone for good.</p>
            </header>

            {deleted.isPending && <SkeletonList rows={3} />}
            {deleted.isError && <ErrorBox error={deleted.error} onRetry={() => void deleted.refetch()} />}
            {deleted.data && deleted.data.items.length === 0 && (
                <EmptyState icon="journal" tone="plain" title="Nothing here.">
                    Deleted tells wait here for a month in case you change your mind.
                </EmptyState>
            )}
            {deleted.data && deleted.data.items.length > 0 && (
                <ul className="rows m-0 list-none p-0">
                    {deleted.data.items.map(post => (
                        <li key={post.id} className="flex flex-col gap-2 py-2">
                            <TellRow post={post} to={`/tell/${post.id}`} trailing={<span className="meta shrink-0">{daysLeft(post.deletedAt!, deleted.data.keepDays)} days left</span>} />
                            <div className="flex gap-2 pl-[60px]">
                                <Button variant="secondary" size="sm" busyLabel="Restoring…" onClick={() => restore.mutateAsync(post.id).then(() => toast.success('Restored.'))}>
                                    Restore
                                </Button>
                                <Button variant="secondary" size="sm" danger onClick={() => deleteForever(post.id, post.title || 'this tell')}>
                                    Delete forever
                                </Button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
